import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getCategoryTheme } from '@/utils/categoryThemes';
import { Skeleton } from '@/components/ui/skeleton';

interface CourseImageCardProps {
  category?: string | null;
  imageUrl?: string | null;
  className?: string;
  showCategoryLabel?: boolean;
}

const SUPABASE_URL = 'https://wxgazidevakcnkrngykk.supabase.co';

export const CourseImageCard: React.FC<CourseImageCardProps> = ({
  category,
  imageUrl,
  className,
  showCategoryLabel = true
}) => {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);
  
  const theme = getCategoryTheme(category);
  const IconComponent = theme.icon;
  
  useEffect(() => {
    setLoading(true);
    setHasError(false);
    
    // Priority: 1. Direct imageUrl prop, 2. Category-based thumbnail from storage
    if (imageUrl && !imageUrl.includes('placeholder')) {
      setResolvedImageUrl(imageUrl);
      setLoading(false);
      return;
    }
    
    // Try to load from category-based storage
    if (category) {
      const categoryFileName = category.toLowerCase().replace(/\s+/g, '-');
      const storageUrl = `${SUPABASE_URL}/storage/v1/object/public/course-thumbnails/${categoryFileName}.png`;
      
      // Check if image exists by attempting to load it
      const img = new Image();
      img.onload = () => {
        setResolvedImageUrl(storageUrl);
        setLoading(false);
      };
      img.onerror = () => {
        setResolvedImageUrl(null);
        setLoading(false);
      };
      img.src = storageUrl;
    } else {
      setLoading(false);
    }
  }, [imageUrl, category]);
  
  // Loading skeleton
  if (loading) {
    return (
      <div className={cn('relative overflow-hidden', className)}>
        <Skeleton className="w-full h-full" />
      </div>
    );
  }
  
  // Show image if available and no error
  if (resolvedImageUrl && !hasError) {
    return (
      <div className={cn('relative overflow-hidden', className)}>
        <img
          src={resolvedImageUrl}
          alt={category || 'Course thumbnail'}
          className="w-full h-full object-cover"
          onError={() => setHasError(true)}
          loading="lazy"
        />
        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        
        {/* Category label */}
        {showCategoryLabel && category && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
            <div className={cn('p-1 rounded', theme.iconBg)}>
              <IconComponent className="h-3 w-3 text-white" />
            </div>
            <span className="text-xs font-medium text-white/90 capitalize">
              {category}
            </span>
          </div>
        )}
      </div>
    );
  }
  
  // Fallback to gradient with icon
  return (
    <div 
      className={cn(
        'relative overflow-hidden flex flex-col items-center justify-center bg-gradient-to-br',
        theme.gradient,
        className
      )}
    >
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
      </div>
      
      {/* Icon and label */}
      <div className="relative z-10 flex flex-col items-center">
        <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm">
          <IconComponent className="h-8 w-8 text-white drop-shadow-lg" />
        </div>
        {showCategoryLabel && category && (
          <span className="mt-2 text-xs font-medium text-white/90 capitalize">
            {category}
          </span>
        )}
      </div>
    </div>
  );
};

export default CourseImageCard;
